"use client";

import {
	Delegation,
	DelegationChain,
	DelegationIdentity,
	Ed25519KeyIdentity,
} from "@icp-sdk/core/identity";
import { Principal } from "@icp-sdk/core/principal";
import React, {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";
import { createLogAdapter } from "@/lib/logger/console-adapter";
import { SiwbAuthRefreshService } from "@/lib/services/siwb-auth-refresh";
import { SessionKeyEncryption } from "@/lib/utils/session-key-encryption";

export type IdentityAuthStatus = "anonymous" | "restoring" | "ready";

interface IdentityContextType {
	identity: DelegationIdentity | null;
	isAuthenticated: boolean;
	authStatus: IdentityAuthStatus;
	principal: string;
	btcAddress: string;
	walletType: string;
	setAuthData: (
		principal: string,
		address: string,
		identity?: DelegationIdentity,
	) => void;
	clearAuth: () => void;
	rebuildIdentity: () => Promise<DelegationIdentity | null>;
	refreshAuth: () => Promise<{
		success: boolean;
		error?: string;
		needsFullLogin?: boolean;
	}>;
}

const IdentityContext = createContext<IdentityContextType | undefined>(
	undefined,
);
const uiLog = createLogAdapter("contexts/IdentityContext.tsx");

interface IdentityProviderProps {
	children: ReactNode;
}

// 根据委托链创建最终身份的辅助函数
function createDelegationChainFromGetResult(
	getResult: any,
	middlePubkeyDer: Uint8Array,
): DelegationChain {
	const delegations = [
		{
			delegation: new Delegation(
				getResult.delegation.pubkey,
				getResult.delegation.expiration,
				getResult.delegation.targets
					? getResult.delegation.targets[0]
					: undefined,
			),
			signature: getResult.signature,
		},
	];
	// 使用传入的 middlePubkeyDer 构建委托链
		return DelegationChain.fromDelegations(delegations, middlePubkeyDer);
}

export function IdentityProvider({ children }: IdentityProviderProps) {
	const [identity, setIdentity] = useState<DelegationIdentity | null>(null);
	const [principal, setPrincipal] = useState<string>("");
	const [btcAddress, setBtcAddress] = useState<string>("");
	const [walletType, setWalletType] = useState<string>("");
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [authStatus, setAuthStatus] =
		useState<IdentityAuthStatus>("anonymous");

	// 从 localStorage 重建身份（使用与 siwb.ts 相同的逻辑）
	const rebuildIdentity = async (): Promise<DelegationIdentity | null> => {
		try {
			// 从 localStorage 获取存储的会话数据
			const storedSessionKey = localStorage.getItem("user_session_key");
			const storedDelegationResult = localStorage.getItem(
				"user_delegation_result",
			);
			const storedPrincipal = localStorage.getItem("user_principal");

			if (!storedSessionKey || !storedDelegationResult || !storedPrincipal) {
				//console.warn('重建身份失败: 缺少必要的存储数据');
				return null;
			}

			// 尝试迁移旧的未加密密钥
			await SessionKeyEncryption.migrateUnencryptedKey(storedPrincipal);

			// 解密会话私钥
			const decryptedPrivateKey =
				await SessionKeyEncryption.decryptSessionKey(storedPrincipal);
			if (!decryptedPrivateKey) {
				uiLog.warn("解密会话私钥失败，清理损坏的加密数据");
				// 清理损坏的加密数据，允许用户重新登录
				SessionKeyEncryption.clearEncryptedData();
				localStorage.removeItem("user_session_key");
				localStorage.removeItem("user_delegation_result");
				localStorage.removeItem("siwb_authenticated");
				return null;
			}

			// 重建会话身份
			const sessionPrivateKeyBuffer = new Uint8Array(
				Buffer.from(decryptedPrivateKey, "hex"),
			);
		const sessionIdentity =
			Ed25519KeyIdentity.fromSecretKey(sessionPrivateKeyBuffer);

			// 解析委托结果，处理 BigInt 字符串
			const parsedChain = JSON.parse(storedDelegationResult, (key, value) => {
				// 将 expiration 字段从字符串转回 BigInt
				if (key === "expiration" && typeof value === "string") {
					return BigInt(value);
				}
				return value;
			});

			// 验证委托数据结构
			if (
				!parsedChain ||
				!parsedChain.delegationData ||
				!parsedChain.delegationData.delegation ||
				!parsedChain.delegationData.signature ||
				!parsedChain.userCanisterPubkey
			) {
				//console.error('重建身份失败: 无效的委托数据结构');
				return null;
			}

			// 提取委托链的根公钥
			const userCanisterPubkeyDer = new Uint8Array(
				Object.values(parsedChain.userCanisterPubkey) as number[],
			);
			const rawDelegationData = parsedChain.delegationData;

			// 将对象形式的 Buffer/Uint8Array 转回 Uint8Array
			const delegationPubkey = new Uint8Array(
				Object.values(rawDelegationData.delegation.pubkey) as number[],
			);
			const signature = new Uint8Array(
				Object.values(rawDelegationData.signature) as number[],
			);

			// Delegation 构造函数期望 targets 是 Principal[]
			const rawTargets = rawDelegationData.delegation.targets?.[0];
			const targets = rawTargets?.map((t: any) =>
				Principal.fromText(t.__principal__),
			);

				const delegation = new Delegation(
					delegationPubkey,
					BigInt(rawDelegationData.delegation.expiration),
					targets,
				);

			const delegations: any = [
				{
					delegation: delegation,
					signature: signature,
				},
			];

				const chain = DelegationChain.fromDelegations(
					delegations,
					userCanisterPubkeyDer,
				);

			const identity = DelegationIdentity.fromDelegation(
				sessionIdentity,
				chain,
			);

			//console.log('重建身份成功:', identity.getPrincipal().toString());
			return identity;
		} catch (error) {
			//console.error('重建身份失败:', error);
			return null;
		}
	};

	// 初始化时检查认证状态
	useEffect(() => {
		const checkAuth = async () => {
			const authenticated =
				localStorage.getItem("siwb_authenticated") === "true";
			const storedPrincipal = localStorage.getItem("user_principal") || "";
			const storedAddress = localStorage.getItem("user_btc_address") || "";
			const storedWalletType = localStorage.getItem("user_wallet_type") || "";
			const storedDelegationResult = localStorage.getItem(
				"user_delegation_result",
			);

			if (
				authenticated &&
				storedPrincipal &&
				storedAddress &&
				storedDelegationResult
			) {
				// 首先检查委托是否过期
				try {
					const parsedChain = JSON.parse(
						storedDelegationResult,
						(key, value) => {
							if (key === "expiration" && typeof value === "string") {
								return BigInt(value);
							}
							return value;
						},
					);

					if (parsedChain?.delegationData?.delegation?.expiration) {
						const expiration = BigInt(
							parsedChain.delegationData.delegation.expiration,
						);
						const now = BigInt(Date.now()) * BigInt(1000000); // 转换为纳秒

						if (expiration <= now) {
							uiLog.warn(
								`【自动登录】【${storedAddress}】身份文件已过期，需要重新登录`,
							);
							clearAuth();
							return;
						}

						// 委托未过期，继续登录流程
						uiLog.info(`【本地登录】【${storedAddress}】...`);
					}
				} catch (error) {
					uiLog.error("检查委托有效期失败:", error);
				}

					setPrincipal(storedPrincipal);
					setBtcAddress(storedAddress);
					setWalletType(storedWalletType);
					setIsAuthenticated(true);
					setAuthStatus("restoring");

					// 尝试重建身份
					const rebuiltIdentity = await rebuildIdentity();
					if (rebuiltIdentity) {
						setIdentity(rebuiltIdentity);
						setAuthStatus("ready");
					} else {
						// 重建失败，清除认证状态
						uiLog.warn(
						"身份重建失败，可能是解密问题，清除认证状态以允许重新登录",
					);
					clearAuth();
				}
			}
		};

		checkAuth();
	}, []);

	// 定期检查委托有效期
	useEffect(() => {
		if (!isAuthenticated) return;

		const checkExpiration = () => {
			const storedDelegationResult = localStorage.getItem(
				"user_delegation_result",
			);
			if (!storedDelegationResult) return;

			try {
				const parsedChain = JSON.parse(storedDelegationResult, (key, value) => {
					if (key === "expiration" && typeof value === "string") {
						return BigInt(value);
					}
					return value;
				});

				if (parsedChain?.delegationData?.delegation?.expiration) {
					const expiration = BigInt(
						parsedChain.delegationData.delegation.expiration,
					);
					const now = BigInt(Date.now()) * BigInt(1000000); // 转换为纳秒

					if (expiration <= now) {
						uiLog.warn("【身份过期】委托已过期，自动退出登录");
						clearAuth();
						// 可选：显示过期提示
						if (typeof window !== "undefined") {
							window.alert("您的登录已过期，请重新登录。");
						}
					}
				}
			} catch (error) {
				uiLog.error("检查委托有效期失败:", error);
			}
		};

		// 立即检查一次
		checkExpiration();

		// 每分钟检查一次
		const interval = setInterval(checkExpiration, 60000);

		return () => clearInterval(interval);
	}, [isAuthenticated]);

	const setAuthData = (
		newPrincipal: string,
		address: string,
		newIdentity?: DelegationIdentity,
	) => {
		setPrincipal(newPrincipal);
		setBtcAddress(address);
		setIsAuthenticated(true);

		// 从localStorage获取钱包类型
		const savedWalletType = localStorage.getItem("user_wallet_type") || "";
		setWalletType(savedWalletType);

		if (newIdentity) {
			setIdentity(newIdentity);
			setAuthStatus("ready");
		} else {
			setAuthStatus("restoring");
		}
	};

	const clearAuth = () => {
		// 清除本地存储
		localStorage.removeItem("siwb_authenticated");
		localStorage.removeItem("user_btc_address");
		localStorage.removeItem("user_principal");
		localStorage.removeItem("user_wallet_type"); // 清除钱包类型
		localStorage.removeItem("user_session_key");
		localStorage.removeItem("user_delegation_result");

		// 清除加密的会话密钥
		SessionKeyEncryption.clearEncryptedData();

		// 清除 odin.fun 授权相关数据
		localStorage.removeItem("odinfun_authtoken");
		localStorage.removeItem("odinfun_authtoken_timestamp");

		// 重置状态
		setIdentity(null);
		setPrincipal("");
		setBtcAddress("");
		setWalletType("");
		setIsAuthenticated(false);
		setAuthStatus("anonymous");
	};

	// 刷新授权
	const refreshAuth = async (): Promise<{
		success: boolean;
		error?: string;
		needsFullLogin?: boolean;
	}> => {
		try {
			uiLog.info("🔄 开始刷新授权...");

			const result = await SiwbAuthRefreshService.refreshAuth({
				showMinimalUI: false, // 由调用方控制UI
				forceFullRefresh: false,
				onProgress: (status: string) => {
					uiLog.debug("📊 刷新进度:", status);
				},
				onError: (errorMessage: string) => {
					uiLog.error("❌ 刷新错误:", errorMessage);
				},
			});

			if (
				result.success &&
				result.identity &&
				result.principal &&
				result.address
			) {
				// 更新全局状态
				setAuthData(result.principal, result.address, result.identity);
				setAuthStatus("ready");
				uiLog.info("✅ 授权刷新成功");
				return { success: true };
			} else {
				uiLog.warn("⚠️ 授权刷新失败:", result.error);
				return {
					success: false,
					error: result.error,
					needsFullLogin: result.needsFullLogin,
				};
			}
		} catch (error: any) {
			uiLog.error("❌ IdentityContext refreshAuth failed:", error);
			return {
				success: false,
				error: error.message || "授权刷新失败",
				needsFullLogin:
					error.message?.includes("重新完整登录") ||
					error.message?.includes("地址已更改"),
			};
		}
	};

	return (
		<IdentityContext.Provider
			value={{
				identity,
				isAuthenticated,
				authStatus,
				principal,
				btcAddress,
				walletType,
				setAuthData,
				clearAuth,
				rebuildIdentity,
				refreshAuth,
			}}
		>
			{children}
		</IdentityContext.Provider>
	);
}

export function useIdentity() {
	const context = useContext(IdentityContext);
	if (context === undefined) {
		throw new Error("useIdentity must be used within an IdentityProvider");
	}
	return context;
}
